import { zodResolver } from '@hookform/resolvers/zod';
import { DialogTitle, DialogContent, Divider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { NewPlanSchema } from '@weft/types';
import { getTomorrow, isNotNullOrUndefined, withDefault } from '@weft/utils';
import { DefaultThemeDialog } from 'apps/app/src/components/design/DefaultThemeDialog';
import { useContract } from 'apps/app/src/hooks/requests/use-contract';
import { useReviseContract } from 'apps/app/src/hooks/requests/use-revise-contract/use-revise-contract';
import { useValidatedParams } from 'apps/app/src/hooks/use-validated-params';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import dayjs from 'dayjs';
import { useFormContext, useForm, Controller } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import type { NewPlanForm } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { ContractReviseDialogActions } from '../../contract-page-components/ContractPageDialogActions';

type SaveReviseProps = {
  open: boolean;
  onClose?: () => void;
};

export const ReviseContractForRenewalFormSchema = z.object({
  startDate: z.date(),
});

export type ReviseContractForRenewalForm = z.infer<typeof ReviseContractForRenewalFormSchema>;

export const SaveReviseRenewalDialog: React.FC<SaveReviseProps> = ({
  open,
  onClose,
}) => {
  const { contractId } = useValidatedParams('contractId');
  const queryClient = useQueryClient();

  const navigate = useVyNavigate();
  const { watch } = useFormContext<PlanInputForm, NewPlanForm>();

  const {
    data: { contract },
  } = useContract(contractId, {
    refetchOnWindowFocus: false,
  });

  const form = useForm<Partial<ReviseContractForRenewalForm>, ReviseContractForRenewalForm>({
    resolver: zodResolver(ReviseContractForRenewalFormSchema),
    defaultValues: {
      startDate: getTomorrow(dayjs(contract.endDate)).toDate(),
    },
  });

  const reviseForRenewalMutation = useReviseContract();

  const handleSubmit = form.handleSubmit(async (data) => {
    const { startDate } = ReviseContractForRenewalFormSchema.parse(data);
    const planInput = watch();
    const fixedProducts = withDefault(planInput?.fixedProducts, []);
    const usageProducts = withDefault(planInput?.usageProducts, []);
    const products = [...fixedProducts, ...usageProducts];

    const parsedNewPlan = NewPlanSchema.safeParse({
      ...planInput,
      products,
    });

    if (!parsedNewPlan.success) {
      return;
    }

    await runMutation(reviseForRenewalMutation, 
      {
        contractId,
        reviseContractRequest: {
          startDate,
          plan: {
            ...parsedNewPlan.data,
            billingData: {
              ...parsedNewPlan.data.billingData,
            },
          },
        },
      }, {
        pending: 'Updating...',
        success: 'Updated',
        error: 'Failed to revise contract',
      }, {
        onSuccess: ({ contract: revisedContract }) => {
          handleClose();
          navigate(`/plans/contracts/${revisedContract.id}`);
    
          return queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.includes(contractId),
          });
        },
      });
  });

  const handleClose = () => {
    onClose?.();
    form.reset();
  };

  return (
    <>
      <DefaultThemeDialog
        open={open}
        onClose={handleClose}
        fullWidth
        PaperProps={{
          className: 'max-w-[780px]',
        }}
      >
        <DialogTitle>Renew contract with new terms</DialogTitle>

        <DialogContent>
          <Divider className="my-6" />
          <div className="flex flex-col gap-y-4">
            <div className="flex">
              <Controller
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <DatePicker
                    minDate={getTomorrow(dayjs(contract.endDate))}
                    value={dayjs(field.value)}
                    onChange={(newDate) => {
                      if (isNotNullOrUndefined(newDate)) {
                        const startOfNewDate = dayjs(newDate).startOf('day').utc(true).toDate();
                        form.setValue('startDate', startOfNewDate);
                      }
                    } }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            background: '#F4FBFF',
                            borderRadius: '8px',
                            paddingLeft: '8px',
                            '& fieldset': {
                              borderColor: '#EAF2F7',
                            },
                            '&:hover fieldset': {
                              borderColor: '#EAF2F7',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#EAF2F7',
                            },
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#588DF7',
                          },
                        },
                      },
                    }}
                  />
                )}
              />
            </div>
            <Divider className="my-6" />

          </div>
        </DialogContent>
        
        <ContractReviseDialogActions
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          text="Renew"
        />
      </DefaultThemeDialog>
    </>
  );
};
